import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryKey } from 'src/query-client';
import { API } from '../api';
import { IPagination } from '../IPagination';
import { CreatePjmLocationDto, PjmLocationDto, UpdatePjmLocationDto, UpdatePjmRegistrationLocationDto } from './dto/PjmLocationDto';
import { CreatePjmRegistrationDto, PjmRegistrationDto, UpdatePjmRegistrationDto } from './dto/PjmRegistrationDto';
import { format } from 'date-fns';
import saveAs from 'file-saver';
import _ from 'lodash';

interface IPjmLocationsQuery {
    pagination?: {
        pageSize?: number;
        current?: number;
    };
    sorter?: {
        field?: string;
        order?: string;
    };
    filter?: Record<string, any>;
    include?: string;
    search?: string;
}

export const fetchPjmLocationsList = async (pageQuery: IPjmLocationsQuery, signal?: AbortSignal): Promise<IPagination<PjmLocationDto>> => {
    const { pagination, filter, sorter, search, include } = pageQuery;

    const _filter: Record<string, string> = {};
    if (filter?.id) {
        _filter.id = filter?.id;
    }

    if (filter?.summaryInfo?.status) {
        _filter.status = filter?.summaryInfo?.status;
    }

    if (filter?.summaryInfo?.zone) {
        _filter.zone = filter?.summaryInfo?.zone;
    }

    if (filter?.siteInfo?.address?.state) {
        _filter.state = filter?.siteInfo?.address?.state;
    }

    if (filter?.siteInfo?.address?.zipCode) {
        _filter.zipCode = filter?.siteInfo?.address?.zipCode;
    }

    if (filter?.summaryInfo?.EDC?.name) {
        _filter.edcName = filter?.summaryInfo?.EDC?.name;
    }

    if (filter?.syncDrhubStatus) {
        _filter.syncDrhubStatus = filter?.syncDrhubStatus;
    }

    if (sorter && _.isArray(sorter?.field)) {
        sorter.field = sorter.field.join('.');
    }

    const limit = pagination?.pageSize || 10;
    const current = pagination?.current || 1;
    const offset = (current - 1) * limit;

    const path = `/pjm/locations?${new URLSearchParams({
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        ..._filter,
        ...(search && { search }),
        ...(include && { include }),
        offset: offset.toString(),
        limit: limit.toString(),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        const items = respBody as IPagination<PjmLocationDto>;
        return items;
    }

    throw new Error(respBody?.error?.message || `Cannot get PJM locations!`);
};

const exportPjmLocationsList = async (pageQuery: IPjmLocationsQuery, signal?: AbortSignal) => {
    const { filter, sorter, search, include } = pageQuery;

    const _filter: Record<string, string> = {};
    if (filter?.id) {
        _filter.id = filter?.id;
    }

    if (filter?.summaryInfo?.status) {
        _filter.status = filter?.summaryInfo?.status;
    }

    if (filter?.summaryInfo?.zone) {
        _filter.zone = filter?.summaryInfo?.zone;
    }

    if (filter?.siteInfo?.address?.state) {
        _filter.state = filter?.siteInfo?.address?.state;
    }

    if (filter?.siteInfo?.address?.zipCode) {
        _filter.zipCode = filter?.siteInfo?.address?.zipCode;
    }

    if (filter?.summaryInfo?.EDC?.name) {
        _filter.edcName = filter?.summaryInfo?.EDC?.name;
    }

    if (filter?.syncDrhubStatus) {
        _filter.syncDrhubStatus = filter?.syncDrhubStatus;
    }

    if (sorter && _.isArray(sorter?.field)) {
        sorter.field = sorter.field.join('.');
    }

    const path = `/pjm/locations?${new URLSearchParams({
        format: 'xlsx',
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        ..._filter,
        ...(search && { search }),
        ...(include && { include }),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error(`Cannot export PJM locations!`);
    }

    const responseBlob = await response.blob();
    const filename = `export-pjm-locations--${format(new Date(), 'MM-dd-yyyy')}.xlsx`;

    saveAs(responseBlob, filename);
    return responseBlob;
};

const createPjmLocation = async (dto: CreatePjmLocationDto): Promise<PjmLocationDto> => {
    const response = await API.fetch('/pjm/locations', {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmLocationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot create PJM location!`);
};

const updatePjmLocation = async (uuid: string, dto: UpdatePjmLocationDto): Promise<PjmLocationDto> => {
    const response = await API.fetch(`/pjm/locations/${uuid}`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmLocationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot update PJM location!`);
};

const updatePjmRegistrationLocation = async (uuid: string, dto: UpdatePjmRegistrationLocationDto): Promise<PjmRegistrationDto> => {
    const response = await API.fetch(`/pjm/registrations/${uuid}/location`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmRegistrationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot update PJM location!`);
};

export const usePjmLocationsListQuery = (query: IPjmLocationsQuery, options = {}) => {
    return useQuery<IPagination<PjmLocationDto>, Error>({
        queryKey: [QueryKey.PJM_LOCATIONS, query],
        queryFn: ({ signal }) => fetchPjmLocationsList(query, signal),
        ...options,
    });
};

export const usePjmLocationCreateMutation = () => {
    return useMutation<PjmLocationDto, Error, CreatePjmLocationDto>({
        mutationFn: dto => createPjmLocation(dto),
    });
};

export const usePjmLocationUpdateMutation = () => {
    return useMutation<PjmLocationDto, Error, { uuid: string; dto: UpdatePjmLocationDto }>({
        mutationFn: ({ uuid, dto }) => updatePjmLocation(uuid, dto),
    });
};

export const usePjmRegistrationLocationUpdateMutation = () => {
    return useMutation<PjmRegistrationDto, Error, { uuid: string; dto: UpdatePjmRegistrationLocationDto }>({
        mutationFn: ({ uuid, dto }) => updatePjmRegistrationLocation(uuid, dto),
    });
};

const createPjmRegistration = async (dto: CreatePjmRegistrationDto): Promise<PjmRegistrationDto> => {
    const response = await API.fetch('/pjm/registrations', {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmRegistrationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot create PJM registration!`);
};

const updatePjmRegistration = async (uuid: string, dto: UpdatePjmRegistrationDto): Promise<PjmRegistrationDto> => {
    const response = await API.fetch(`/pjm/registrations/${uuid}`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmRegistrationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot update PJM registration!`);
};

const submitPjmRegistration = async (uuid: string): Promise<PjmRegistrationDto> => {
    const response = await API.fetch(`/pjm/registrations/${uuid}/submit`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmRegistrationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot submit PJM registration!`);
};

const withdrawPjmRegistration = async (uuid: string): Promise<PjmRegistrationDto> => {
    const response = await API.fetch(`/pjm/registrations/${uuid}/withdraw`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmRegistrationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot withdraw PJM registration!`);
};

const terminatePjmRegistration = async (uuid: string): Promise<PjmRegistrationDto> => {
    const response = await API.fetch(`/pjm/registrations/${uuid}/terminate`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmRegistrationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot terminate PJM registration!`);
};

const calculateWplForPjmRegistration = async (uuid: string): Promise<PjmRegistrationDto> => {
    const response = await API.fetch(`/pjm/registrations/${uuid}/calculate-wpl`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmRegistrationDto;
    }

    throw new Error(respBody?.error?.message || `Cannot calculate WPL for PJM registration!`);
};

interface IPjmRegistrationQuery {
    pagination?: {
        pageSize?: number;
        current?: number;
    };
    sorter?: {
        field?: string;
        order?: string;
    };
    filter?: Record<string, any>;
    include?: string;
    search?: string;
}

const fetchPjmRegistrationsList = async (pageQuery: IPjmRegistrationQuery, signal?: AbortSignal): Promise<IPagination<PjmRegistrationDto>> => {
    const { pagination, filter, sorter, search, include } = pageQuery;

    const _filter: Record<string, string> = {};
    if (filter?.summaryInfo?.status) {
        _filter.status = filter?.summaryInfo?.status;
    }

    if (filter?.summaryInfo?.zone) {
        _filter.zone = filter?.summaryInfo?.zone;
    }

    if (filter?.summaryInfo?.EDC?.name) {
        _filter.edcName = filter?.summaryInfo?.EDC?.name;
    }

    if (filter?.capacityInfo?.resourceType) {
        _filter.resourceType = filter?.capacityInfo?.resourceType;
    }

    if (filter?.capacityInfo?.program) {
        _filter.program = filter?.capacityInfo?.program;
    }

    if (filter?.capacityInfo?.product) {
        _filter.product = filter?.capacityInfo?.product;
    }

    if (filter?.capacityInfo?.leadTime) {
        _filter.leadTime = filter?.capacityInfo?.leadTime;
    }

    if (filter?.summaryInfo?.completed) {
        _filter.completed = filter?.summaryInfo?.completed;
    }

    if (filter?.summaryInfo?.dispatchGroup) {
        _filter.dispatchGroup = filter?.summaryInfo?.dispatchGroup;
    }

    if (filter?.summaryInfo?.underDuplicateReview) {
        _filter.underDuplicateReview = filter?.summaryInfo?.underDuplicateReview;
    }

    if (filter?.season) {
        _filter.season = filter?.season;
    }

    if (filter?.syncDrhubStatus) {
        _filter.syncDrhubStatus = filter?.syncDrhubStatus;
    }

    if (sorter && _.isArray(sorter?.field)) {
        sorter.field = sorter.field.join('.');
    }

    const limit = pagination?.pageSize || 10;
    const current = pagination?.current || 1;
    const offset = (current - 1) * limit;

    const path = `/pjm/registrations?${new URLSearchParams({
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        ..._filter,
        ...(search && { search }),
        ...(include && { include }),
        offset: offset.toString(),
        limit: limit.toString(),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        const items = respBody as IPagination<PjmRegistrationDto>;
        return items;
    }

    throw new Error(respBody?.error?.message || `Cannot get PJM registrations!`);
};

export const fetchPjmRegistration = async (uuid: string, signal?: AbortSignal): Promise<PjmRegistrationDto> => {
    const path = `/pjm/registrations/${uuid}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        const item = respBody as PjmRegistrationDto;
        return item;
    }

    throw new Error(respBody?.error?.message || `Cannot get PJM registration!`);
};

export const usePjmRegistrationsListQuery = (query: IPjmRegistrationQuery, options = {}) => {
    return useQuery<IPagination<PjmRegistrationDto>, Error>({
        queryKey: [QueryKey.PJM_REGISTRATIONS, query],
        queryFn: ({ signal }) => fetchPjmRegistrationsList(query, signal),
        ...options,
    });
};

export const usePjmRegistrationQuery = (uuid: string, options = {}) => {
    return useQuery<PjmRegistrationDto, Error>({
        queryKey: [QueryKey.PJM_REGISTRATIONS, { uuid }],
        queryFn: ({ signal }) => fetchPjmRegistration(uuid, signal),
        ...options,
    });
};

export const useExportPjmLocationsQuery = (query: IPjmLocationsQuery) => {
    return useQuery<Blob, Error>([QueryKey.PJM_LOCATIONS, 'export-xlsx', query], () => exportPjmLocationsList(query), {
        enabled: false,
    });
};

export const usePjmRegistrationCreateMutation = () => {
    return useMutation<PjmRegistrationDto, Error, CreatePjmRegistrationDto>({
        mutationFn: dto => createPjmRegistration(dto),
    });
};

export const usePjmRegistrationUpdateMutation = () => {
    return useMutation<PjmRegistrationDto, Error, { uuid: string; dto: UpdatePjmRegistrationDto }>({
        mutationFn: ({ uuid, dto }) => updatePjmRegistration(uuid, dto),
    });
};

export const usePjmRegistrationSubmitMutation = () => {
    return useMutation<PjmRegistrationDto, Error, { uuid: string }>({
        mutationFn: ({ uuid }) => submitPjmRegistration(uuid),
    });
};

export const usePjmRegistrationWithdrawMutation = () => {
    return useMutation<PjmRegistrationDto, Error, { uuid: string }>({
        mutationFn: ({ uuid }) => withdrawPjmRegistration(uuid),
    });
};

export const usePjmRegistrationTerminateMutation = () => {
    return useMutation<PjmRegistrationDto, Error, { uuid: string }>({
        mutationFn: ({ uuid }) => terminatePjmRegistration(uuid),
    });
};

export const usePjmRegistrationCalculateWplMutation = () => {
    return useMutation<PjmRegistrationDto, Error, { uuid: string }>({
        mutationFn: ({ uuid }) => calculateWplForPjmRegistration(uuid),
    });
};

export type PjmPollingRequestType = 'load-management-events' | 'load-management-events:test';

export interface PjmPollingStatusItemDto {
    datetime: string;
    type: PjmPollingRequestType;
    success: boolean;
    result: string;
}

const fetchPjmPollingStatus = async (signal?: AbortSignal): Promise<PjmPollingStatusItemDto[]> => {
    const response = await API.fetch('/pjm/polling/status', {
        ...(signal && { signal }),
        method: 'get',
        headers: { 'Content-Type': 'application/json' },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody as PjmPollingStatusItemDto[];
    }

    throw new Error(respBody?.error?.message || 'Cannot get PJM polling status!');
};

export const usePjmPollingStatusQuery = (options = {}) => {
    return useQuery<PjmPollingStatusItemDto[], Error>({
        queryKey: [QueryKey.PJM_POLLING_STATUS],
        queryFn: ({ signal }) => fetchPjmPollingStatus(signal),
        ...options,
    });
};
